import { FC, forwardRef, InputHTMLAttributes, LegacyRef, ChangeEvent } from 'react';
import { cn } from '@/utils/classNames.ts';
import { InputProps } from 'react-daisyui';
type FileInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'onChange' | 'value'> &
  Pick<InputProps, 'size' | 'color' | 'bordered'> & {
    label?: string;
    error?: string;
    wrapperClassName?: string;
    hideError?: boolean;
    onChange?: (files: File[]) => void;
    value?: File | File[];
  } & { ref?: LegacyRef<HTMLInputElement> };

const FileInput: FC<FileInputProps> = forwardRef(
  (
    {
      label,
      error,
      wrapperClassName,
      size = 'md',
      color,
      bordered = true,
      hideError = false,
      onChange,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      value,
      ...args
    },
    ref
  ) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files) {
        const fileArray = Array.from(files);
        if (onChange) {
          onChange(fileArray);
        }
      }
    };

    return (
      <div className={cn('form-control max-w-full w-full', wrapperClassName)}>
        {label ? (
          <label className="label">
            <span
              className={cn('label-text', {
                'text-ghost': color === 'ghost',
                'text-primary': color === 'primary',
                'text-secondary': color === 'secondary',
                'text-accent': color === 'accent',
                'text-info': color === 'info',
                'text-success': color === 'success',
                'text-warning': color === 'warning',
              })}
            >
              {label}
            </span>
          </label>
        ) : null}
        <input
          type="file"
          className={cn('file-input', {
            'file-input-ghost': color === 'ghost',
            'file-input-primary': color === 'primary',
            'file-input-secondary': color === 'secondary',
            'file-input-accent': color === 'accent',
            'file-input-info': color === 'info',
            'file-input-success': color === 'success',
            'file-input-warning': color === 'warning',
            'file-input-error': error || color === 'error',
            'file-input-xs': size === 'xs',
            'file-input-sm': size === 'sm',
            'file-input-md': size === 'md' || size,
            'file-input-lg': size === 'lg',
            'file-input-bordered': bordered,
          })}
          ref={ref}
          onChange={handleChange}
          {...args}
        />
        {!hideError && !!error ? (
          <label className="label">
            <span className="label-text-alt text-error">{error}</span>
          </label>
        ) : null}
      </div>
    );
  }
);

export default FileInput;
